import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { useIntl } from "react-intl";
import {
  Box,
  ButtonBase,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Stack,
  Tooltip,
} from "@mui/material";

import EditOutlinedIcon from "@mui/icons-material/EditOutlined";

const ViewNavigation = ({
  views = [],
  groups = [],
  activeViewId,
  openGroupId,
  openGroupAnchorEl,
  pinnedGroupId,
  onAddMenuOpen,
  onGroupHover,
  onGroupClick,
  onNavigationLeave,
  onFloatingEnter,
  onFloatingLeave,
  onCloseFloating,
  onViewSelect,
  onEditGroupClick = () => { },
  onEditViewClick = () => { },
}) => {
  const intl = useIntl();

  const rootViews = views.filter((v) => !v.groupId);
  const openGroup = groups.find((g) => g.id === openGroupId) ?? null;
  const openGroupViews = openGroup
    ? views.filter((v) => v.groupId === openGroup.id)
    : [];
  const floatingOpen = Boolean(openGroup && openGroupAnchorEl);

  const activeView = views.find((v) => v.id === activeViewId);
  const activeGroupId = activeView?.groupId ?? null;

 
  return (
    <ClickAwayListener onClickAway={onCloseFloating}>
      <Box sx={{ position: "relative" }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.25}
          onMouseLeave={onNavigationLeave}
          sx={{
            minHeight: 26,
            borderBottom: "1px solid",
            borderColor: "rgba(0,0,0,0.08)",
          }}
        >
          {groups.map((g) => {
            const isActive = activeGroupId === g.id;
            const isOpen = openGroupId === g.id;
            const isPinned = pinnedGroupId === g.id;

            return (
              <Box
                key={g.id}
                sx={{
                  display: "flex",
                  alignItems: "center",

                  "& .edit-button": {
                    opacity: isActive ? 1 : 0,
                  },

                  "&:hover .edit-button": {
                    opacity: 1,
                  },
                }}
              >
                <ButtonBase
                  disableRipple
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => onGroupClick(g.id, e.currentTarget)}
                  onMouseEnter={(e) => onGroupHover(g.id, e.currentTarget)}
                  aria-haspopup="true"
                  aria-expanded={isOpen ? "true" : undefined}
                  sx={{
                    minHeight: 26,
                    px: 1.5,
                    position: "relative",
                    borderRadius: "6px 6px 0 0",
                    color: isActive ? "primary.dark" : "text.secondary",
                    fontSize: 12,
                    fontWeight: isActive ? 500 : 400,
                    lineHeight: 1,
                    transition: "color 120ms ease, background-color 120ms ease",
                    backgroundColor: isOpen ? "action.hover" : "transparent",

                    "&:hover": {
                      color: "primary.main",
                      backgroundColor: "action.hover",
                    },

                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: 12,
                      right: 12,
                      bottom: -1,
                      height: "1px",
                      backgroundColor: isActive ? "primary.main" : "transparent",
                    },
                  }}
                >
                  <Box component="span">{g.name}</Box>

                  <Box
                    component="span"
                    sx={{
                      ml: 0.5,
                      color: isPinned ? "primary.main" : "text.disabled",
                      fontSize: 11,
                      transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                      transition: "transform 120ms ease, color 120ms ease",
                    }}
                  >
                    ▾
        </Box>
                </ButtonBase>

                <IconButton
                  className="edit-button"
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditGroupClick?.(g);
                  }}
                  sx={{
                    width: 18,
                    height: 18,
                    ml: 0.25,
                    color: "text.secondary",
                    transition: "opacity 120ms ease",

                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            );
          })}


          {rootViews.map((v) => {
            const isActive = activeViewId === v.id;

            return (
              <Box
                key={v.id}
                sx={{
                  display: "flex",
                  alignItems: "center",

                  "& .edit-button": {
                    opacity: isActive ? 1 : 0,
                  },

                  "&:hover .edit-button": {
                    opacity: 1,
                  },
                }}
              >
                <ButtonBase
                  disableRipple
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onViewSelect(v.id, null)}
                  sx={{
                    minHeight: 26,
                    px: 1.5,
                    position: "relative",
                    borderRadius: "6px 6px 0 0",
                    color: isActive ? "primary.dark" : "text.secondary",
                    fontSize: 12,
                    fontWeight: isActive ? 500 : 400,
                    lineHeight: 1,
                    transition: "color 120ms ease, background-color 120ms ease",

                    "&:hover": {
                      color: "primary.main",
                      backgroundColor: "action.hover",
                    },

                    "&::after": {
                      content: '""',
                      position: "absolute",
                      left: 12,
                      right: 12,
                      bottom: -1,
                      height: "1px",
                      backgroundColor: isActive ? "primary.main" : "transparent",
                    },
                  }}
                >
                  {v.name}
                </ButtonBase>

                <IconButton
                  className="edit-button"
                  size="small"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditViewClick?.(v);
                  }}
                  sx={{
                    width: 18,
                    height: 18,
                    ml: 0.25,
                    color: "text.secondary",
                    transition: "opacity 120ms ease",

                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 12 }} />
                </IconButton>
              </Box>
            );
          })}

          <Tooltip title={intl.formatMessage({ id: "incomingOrders.views.create" })}>
            <IconButton
              size="small"
              disableRipple
              onMouseDown={(e) => e.preventDefault()}
              onClick={onAddMenuOpen}
              aria-label={intl.formatMessage({ id: "incomingOrders.views.createViewOrGroup" })}
              sx={{
                width: 26,
                height: 26,
                ml: 0.25,
                opacity: 0.35,
                color: "text.secondary",
                "&:hover": { opacity: 0.75, backgroundColor: "action.hover" },
              }}
            >
              <AddRoundedIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        </Stack>

        <Popper
          open={floatingOpen}
          anchorEl={openGroupAnchorEl}
          placement="bottom-start"
          modifiers={[
            { name: "offset", options: { offset: [0, 5] } },
            { name: "preventOverflow", options: { padding: 12 } },
          ]}
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Paper
            elevation={6}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={onFloatingEnter}
            onMouseLeave={onFloatingLeave}
            sx={{
              px: 0.5,
              py: 0.5,
              borderRadius: "10px",
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              boxShadow: "0 8px 28px rgba(0,0,0,0.14)",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.25}>
              {openGroupViews.map((view) => {
                const selected = view.id === activeViewId;

                return (
                  <Box
                    key={view.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",

                      "& .edit-button": {
                        opacity: selected ? 1 : 0,
                      },

                      "&:hover .edit-button": {
                        opacity: 1,
                      },
                    }}
                  >
                    <ButtonBase
                      disableRipple
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => onViewSelect(view.id, openGroup.id)}
                      sx={{
                        minHeight: 28,
                        px: 1.5,
                        position: "relative",
                        borderRadius: "7px",
                        color: selected ? "primary.dark" : "text.secondary",
                        fontSize: 12,
                        fontWeight: selected ? 500 : 400,
                        whiteSpace: "nowrap",
                        transition: "color 120ms ease, background-color 120ms ease",
                        backgroundColor: selected ? "action.selected" : "transparent",

                        "&:hover": {
                          color: "primary.main",
                          backgroundColor: "action.hover",
                        },

                        "&::after": {
                          content: '""',
                          position: "absolute",
                          left: 12,
                          right: 12,
                          bottom: 2,
                          height: "1px",
                          backgroundColor: selected ? "primary.main" : "transparent",
                        },
                      }}
                    >
                      {view.name}
                    </ButtonBase>

                    <IconButton
                      className="edit-button"
                      size="small"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditViewClick?.(view);
                      }}
                      sx={{
                        width: 18,
                        height: 18,
                        ml: 0.25,
                        color: "text.secondary",
                        transition: "opacity 120ms ease",

                        "&:hover": {
                          backgroundColor: "action.hover",
                        },
                      }}
                    >
                      <EditOutlinedIcon sx={{ fontSize: 12 }} />
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default ViewNavigation;